import { useState, useRef, useEffect, useCallback } from "react";
import { BaseAppProps } from "../../../types/webos";

import rougeSound    from "url:./sounds/rouge.mp3";
import bleuSound     from "url:./sounds/bleu.mp3";
import vertSound     from "url:./sounds/vert.mp3";
import jauneSound    from "url:./sounds/jaune.mp3";
import orangeSound   from "url:./sounds/orange.mp3";
import violetSound   from "url:./sounds/violet.mp3";
import roseSound     from "url:./sounds/rose.mp3";
import blancSound    from "url:./sounds/blanc.mp3";
import marronSound   from "url:./sounds/marron.mp3";
import noirSound     from "url:./sounds/noir.mp3";
import grisSound     from "url:./sounds/gris.mp3";
import turquoiseSound from "url:./sounds/turquoise.mp3";
import doreSound     from "url:./sounds/dore.mp3";
import indigoSound   from "url:./sounds/indigo.mp3";
import cyanSound     from "url:./sounds/cyan.mp3";
import corailSound   from "url:./sounds/corail.mp3";

interface ColorDef {
  name: string;
  hex: string;
  text: string;
  sound: string;
}

const COLORS: ColorDef[] = [
  { name: "Rouge",     hex: "#EF4444", text: "white",  sound: rougeSound },
  { name: "Bleu",      hex: "#3B82F6", text: "white",  sound: bleuSound },
  { name: "Vert",      hex: "#22C55E", text: "white",  sound: vertSound },
  { name: "Jaune",     hex: "#EAB308", text: "black",  sound: jauneSound },
  { name: "Orange",    hex: "#F97316", text: "white",  sound: orangeSound },
  { name: "Violet",    hex: "#A855F7", text: "white",  sound: violetSound },
  { name: "Rose",      hex: "#EC4899", text: "white",  sound: roseSound },
  { name: "Blanc",     hex: "#F8FAFC", text: "black",  sound: blancSound },
  { name: "Marron",    hex: "#92400E", text: "white",  sound: marronSound },
  { name: "Noir",      hex: "#1E293B", text: "white",  sound: noirSound },
  { name: "Gris",      hex: "#94A3B8", text: "white",  sound: grisSound },
  { name: "Turquoise", hex: "#14B8A6", text: "white",  sound: turquoiseSound },
  { name: "Doré",      hex: "#D97706", text: "white",  sound: doreSound },
  { name: "Indigo",    hex: "#6366F1", text: "white",  sound: indigoSound },
  { name: "Cyan",      hex: "#06B6D4", text: "white",  sound: cyanSound },
  { name: "Corail",    hex: "#F87171", text: "white",  sound: corailSound },
];

const MIDI_NOTE_TO_INDEX: Record<number, number> = {
  48: 0,  50: 1,  52: 2,  55: 3,
  57: 4,  60: 5,  62: 6,  64: 7,
  67: 8,  69: 9,  72: 10, 74: 11,
  76: 12, 79: 13, 81: 14, 84: 15,
};

export function App({}: BaseAppProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [midiConnected, setMidiConnected] = useState(false);
  const highlightTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const audioRef = useRef<HTMLAudioElement | null>(null);

  const playSound = useCallback((soundUrl: string) => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    const audio = new Audio(soundUrl);
    audioRef.current = audio;
    audio.play().catch(() => {});
  }, []);

  const triggerColor = useCallback(
    (index: number) => {
      if (highlightTimer.current) clearTimeout(highlightTimer.current);
      setActiveIndex(index);
      highlightTimer.current = setTimeout(() => setActiveIndex(null), 1500);

      if (navigator.vibrate) navigator.vibrate(80);
      playSound(COLORS[index].sound);
    },
    [playSound]
  );

  useEffect(() => {
    if (!navigator.requestMIDIAccess) return;
    let access: any;

    const onMessage = (event: any) => {
      const [cmd, note, vel] = event.data;
      if ((cmd & 0xf0) === 0x90 && vel > 0) {
        const idx = MIDI_NOTE_TO_INDEX[note as number];
        if (idx !== undefined) triggerColor(idx);
      }
    };

    const setup = () => {
      for (const input of access.inputs.values()) {
        input.onmidimessage = onMessage;
      }
      setMidiConnected(access.inputs.size > 0);
    };

    (navigator as any).requestMIDIAccess().then((a: any) => {
      access = a;
      setup();
      access.onstatechange = setup;
    }).catch(() => {});

    return () => {
      if (access) {
        for (const input of access.inputs.values()) {
          input.onmidimessage = null;
        }
      }
      if (highlightTimer.current) clearTimeout(highlightTimer.current);
      if (audioRef.current) audioRef.current.pause();
    };
  }, [triggerColor]);

  return (
    <div className="relative h-full overflow-hidden">
      <div className="grid grid-cols-4 grid-rows-4 gap-1.5 h-full p-1.5">
        {COLORS.map((color, i) => (
          <button
            key={i}
            onClick={() => triggerColor(i)}
            className="rounded-2xl flex items-center justify-center text-sm font-black uppercase tracking-wide shadow-md active:scale-95 transition-transform duration-100 select-none touch-manipulation"
            style={{ backgroundColor: color.hex, color: color.text }}
          >
            {color.name}
          </button>
        ))}
      </div>

      {activeIndex !== null && (
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
          style={{ backgroundColor: COLORS[activeIndex].hex, animation: "colorFlash 0.2s ease-out" }}
        >
          <span
            className="text-7xl font-black uppercase tracking-widest drop-shadow-lg"
            style={{ color: COLORS[activeIndex].text }}
          >
            {COLORS[activeIndex].name}
          </span>
        </div>
      )}

      <div className="absolute bottom-2 right-2 flex items-center gap-1.5 bg-black/40 text-white text-xs px-2 py-1 rounded-full pointer-events-none">
        <span className={`w-2 h-2 rounded-full ${midiConnected ? "bg-green-400" : "bg-gray-500"}`} />
        {midiConnected ? "MIDI connecté" : "Pas de MIDI"}
      </div>

      <style>{`
        @keyframes colorFlash {
          from { transform: scale(0.85); opacity: 0.7; }
          to   { transform: scale(1);    opacity: 1;   }
        }
      `}</style>
    </div>
  );
}
